import Phaser from 'phaser';

// LightSkin — a named lighting preset for a Scene (Order 16.2). Modeled on the
// Collage Engine's CollageSkin shape (collage_engine_spec v0): a flat
// id/name record whose remaining fields are grouped sub-objects, so authoring a
// mood is authoring one of these and a Scene never hard-codes a colour. Where
// CollageSkin carries paper / inks / print / motion, a LightSkin carries the
// three things a 2.5D scene's mood is actually made of:
//
//   • ambient — the base wash every normal-mapped ("lit") sprite sits in.
//   • lights  — the light rig: one key plus fills/rims/practicals, world-placed.
//   • grade   — a global camera colour grade (the post pass over the whole frame).
//
// The Scene applies a skin once (applyLightSkin) and then only drives update(t)
// so flickering practicals live. Adding "day-market" or "sodium-underpass" later
// is another exported const of this shape — no Scene code changes.

/** A single light in the rig. `role` is documentation + intent (all lights are
 *  the same Phaser Light under the hood); a `flicker` gives neon/practical life. */
export interface LightDef {
  role: 'key' | 'fill' | 'rim' | 'practical';
  /** World-pixel position of the light. */
  x: number;
  y: number;
  /** Falloff radius in world px. */
  radius: number;
  /** Light colour 0xRRGGBB. */
  color: number;
  /** Base intensity (multiplier on the light's contribution). */
  intensity: number;
  /** Optional flicker: `amount` is peak fractional intensity wobble, `hz` its
   *  rough rate. Deterministic (a sum of sines), so runs stay reproducible. */
  flicker?: { amount: number; hz: number };
}

/** The global colour grade, applied as one camera ColorMatrix filter. Each field
 *  maps to a Phaser Display.ColorMatrix op; they compose in listed order.
 *
 *  Two of Phaser's ops do NOT mean what their names suggest, and this skin shipped
 *  (Order 16.2) assuming they did — which graded the whole frame to black:
 *
 *    • `contrast(v)` is a DELTA, not a multiplier. contrast(0) is the identity and
 *      contrast(0.1) is "+10%". The old skin passed 1.1 meaning "+10%" and got
 *      ×2.1, blowing mid-grey to 1.048.
 *    • `night(v)` is not a cold tint. Even at 0.06 its matrix SUBTRACTS luminance:
 *      it maps mid-grey to (−0.09, 0.00, +0.09). Composed with the rest it pushed
 *      every grey in the frame negative (−0.06 at 10% grey through −0.59 at white),
 *      clamping red to zero everywhere. It is gone, and not coming back: the cold
 *      cast belongs to the cold `ambient` and the blue fill light, where it can't
 *      eat the image.
 *
 *  The grade is now nearly the identity by design. A night scene should be dark
 *  because it is lit like one, not because a post filter crushed it. */
export interface LightGrade {
  /** Overall multiplier (<1 darkens). */
  brightness: number;
  /** Saturation multiplier (<1 mutes). */
  saturate: number;
  /** Hue rotation in degrees (negative → cooler). */
  hueDeg: number;
  /** Contrast DELTA: 0 = neutral, 0.1 = +10%. Not a multiplier. */
  contrast: number;
}

/** The full preset. `schema`/`version` mirror the deform-timeline discriminator
 *  convention so a loaded skin can be validated the same way. */
export interface LightSkin {
  schema: 'gooddesign.light-skin';
  version: 1;
  id: string;
  name: string;
  /** Base illumination for lit sprites, 0xRRGGBB. */
  ambient: number;
  lights: LightDef[];
  grade: LightGrade;
}

// ─────────────────────────────────────────────────────────────────────────────
// The one authored skin for the slice: night alley. A dim cold ambient; a cold
// blue sky FILL from the left; a red practical RIM at the far end; and a
// graded-down, slightly cool, punchier frame. Studio-palette colours (yellow
// #d8c05d, blue #4684b2, red #cc444e).
//
// A skin now carries the PLACELESS mood — the ambient, the grade, and the lights
// with no visible source (the sky, the far red glow). The one light that IS a
// visible object — the marquee KEY — moved to the marquee PROP in the Scene
// (Order 16.5), so the sign and its key light are a single fact that travels
// together when a location places the sign somewhere else. The scene loader
// composes a prop's light back onto the skin at apply time, so the rendered rig
// is unchanged: the same three lights reach the frame, in the same numbers.
// ─────────────────────────────────────────────────────────────────────────────
export const NIGHT_ALLEY: LightSkin = {
  schema: 'gooddesign.light-skin',
  version: 1,
  id: 'night-alley',
  name: 'Night Alley',
  ambient: 0x161c2e, // cold, dim base
  lights: [
    // Sky fill — cold, soft, from up-left so the far side of the body stays read.
    { role: 'fill', x: 90, y: 90, radius: 300, color: 0x4684b2, intensity: 0.75 },
    // Far practical — a red sign deep in the alley, a rim as you approach.
    { role: 'practical', x: 1240, y: 130, radius: 320, color: 0xcc444e, intensity: 0.9, flicker: { amount: 0.1, hz: 5 } },
  ],
  grade: { brightness: 0.92, saturate: 0.95, hueDeg: -6, contrast: 0.08 },
};

// A second authored skin, added to prove the Scene format's "reference a
// different skin by id" path (Order 16.5's second location). This is the
// extension LightSkin.ts always anticipated — "another exported const of this
// shape" — a warm sodium underpass: an amber-brown ambient, a warmer key from the
// sign, and a grade pushed the other way (warm, not cool). No engine code; a new
// mood is new data.
export const SODIUM_UNDERPASS: LightSkin = {
  schema: 'gooddesign.light-skin',
  version: 1,
  id: 'sodium-underpass',
  name: 'Sodium Underpass',
  ambient: 0x241a12, // warm, dim base
  lights: [
    // Fill — a weak cold spill from the street mouth, so the shadows aren't dead.
    { role: 'fill', x: 120, y: 100, radius: 280, color: 0x3a5a72, intensity: 0.5 },
    // Far practical — sodium-orange glow deep under the bridge.
    { role: 'practical', x: 1080, y: 140, radius: 300, color: 0xe08840, intensity: 0.85, flicker: { amount: 0.06, hz: 3 } },
  ],
  grade: { brightness: 0.9, saturate: 1.05, hueDeg: 8, contrast: 0.1 },
};

// The first playable-cinema skin (arcade_playable_cinema_v0): a courtyard at
// dusk, lit to MATCH a photographic plate rather than to illuminate a scene. The
// plate carries its own baked light; this only has to shape the actor so a figure
// crossing the plaza belongs in the frame — a low warm sun hard from screen-right
// (the direction the plate's shadows rake), a weak cool bounce from the shaded
// left, and a warm dim ambient so a figure in shadow stays warm-dark, not black.
export const COURTYARD_DUSK: LightSkin = {
  schema: 'gooddesign.light-skin',
  version: 1,
  id: 'courtyard-dusk',
  name: 'Courtyard Dusk',
  ambient: 0x2a2119, // warm, dim
  lights: [
    // Sun key — low, warm, hard, from screen-right, matching the plate's rake.
    { role: 'key', x: 640, y: 300, radius: 640, color: 0xffd39a, intensity: 1.55 },
    // Cool bounce from the shaded left so the far side of the body still reads.
    { role: 'fill', x: 30, y: 180, radius: 320, color: 0x3a4a63, intensity: 0.4 },
  ],
  grade: { brightness: 1.0, saturate: 1.05, hueDeg: 4, contrast: 0.06 },
};

// ── Application ──────────────────────────────────────────────────────────────

/** Live handle returned by applyLightSkin: the created Lights (so the Scene can
 *  read their positions for verification) and an update(t) that drives flicker. */
export interface AppliedSkin {
  lights: Phaser.GameObjects.Light[];
  defs: LightDef[];
  colorMatrix: Phaser.Filters.ColorMatrix;
  update(timeSec: number): void;
}

/** Enable the scene's lighting, set the ambient, build the rig, and add the
 *  global colour grade as a camera filter. Returns a handle to drive per frame. */
export function applyLightSkin(scene: Phaser.Scene, skin: LightSkin): AppliedSkin {
  scene.lights.enable();
  scene.lights.setAmbientColor(skin.ambient);

  const lights: Phaser.GameObjects.Light[] = [];
  const baseIntensity: number[] = [];
  for (const d of skin.lights) {
    lights.push(scene.lights.addLight(d.x, d.y, d.radius, d.color, d.intensity));
    baseIntensity.push(d.intensity);
  }

  // Global colour grade — one ColorMatrix filter on the camera's internal list.
  const cam = scene.cameras.main;
  const cmFilter = cam.filters.internal.addColorMatrix();
  const m = cmFilter.colorMatrix;
  const g = skin.grade;
  // multiply=true so the ops compose instead of each replacing the matrix.
  m.brightness(g.brightness, true);
  m.saturate(g.saturate, true);
  if (g.hueDeg) m.hue(g.hueDeg, true);
  if (g.contrast) m.contrast(g.contrast, true); // delta: 0 is the identity

  const defs = skin.lights;

  return {
    lights,
    defs,
    colorMatrix: cmFilter,
    update(timeSec: number): void {
      for (let i = 0; i < defs.length; i++) {
        const f = defs[i].flicker;
        if (!f) continue;
        // Deterministic pseudo-flicker: two detuned sines, biased so it mostly
        // sits near full and only dips — reads like a struggling neon tube.
        const w =
          Math.sin(timeSec * f.hz * 2 * Math.PI) * 0.6 +
          Math.sin(timeSec * f.hz * 2 * Math.PI * 2.37 + 1.1) * 0.4;
        lights[i].intensity = baseIntensity[i] * (1 - f.amount * (0.5 - 0.5 * w));
      }
    },
  };
}
